import React from "react";
import { PageHero } from "@/components/PageHero";
import { CTABand } from "@/components/CTABand";
import { Reveal } from "@/components/Reveal";
import { ResourceCard, CustomResourceCard, Eyebrow } from "@/components/ServiceSections";
import { PostCard, useInsights } from "@/pages/Insights";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useResourceFiles } from "@/context/ResourceFiles";
import { SERVICES, ALL_SERVICE_PAGES, IMAGES } from "@/content/services";

export default function Resources() {
  const { custom } = useResourceFiles();
  const { posts } = useInsights();
  const items = ALL_SERVICE_PAGES.flatMap((p) =>
    SERVICES[p].resources.map((r) => ({ ...r, service: SERVICES[p].breadcrumb.at(-1) }))
  );
  return (
    <div data-testid="resources-page">
      <PageHero
        breadcrumb={["Home", "Resources"]}
        pill="Resources"
        titleLines={[[{ t: "Whitepapers and " }, { t: "checklists.", accent: true }]]}
        description="Free, practical guides from Lanvaus engineers: scoping, readiness checklists, compliance mappings and architecture references. Every download is a real PDF, written by the people who deliver the work."
        primaryCta={{ label: "Get a Consultation", to: "/contact" }}
        secondaryCta={{ label: "How Engagements Run", to: "/case-studies" }}
        image={IMAGES.dashboard}
        showCompliance={false}
      />
      <section className="mx-auto max-w-7xl px-5 pb-16 sm:pb-24 pt-16 sm:pt-24 sm:px-8 lg:px-16" data-testid="resources-list">
        <Reveal>
          <Eyebrow>Library</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {items.length + custom.length} documents,{" "}
            <span className="text-[#8E9AEA]">no sales deck among them.</span>
          </h2>
        </Reveal>
        <div className="mt-10 flex flex-col gap-4">
          {items.map((r, i) => (
            <ResourceCard key={r.title} r={r} delay={Math.min(i, 4) * 0.05} service={r.service} />
          ))}
          {custom.map((c, i) => (
            <CustomResourceCard key={c.slotKey} item={c} delay={Math.min(i, 4) * 0.05} showPage />
          ))}
        </div>
      </section>

      <section
        className="relative border-y border-white/10 bg-[#0A0E19]"
        data-testid="resources-blogs"
      >
        <div className="glow-orb -left-32 top-1/2 h-[420px] w-[420px] -translate-y-1/2 bg-[#4C5A8C]/20" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24 lg:px-16">
          <Reveal>
            <Eyebrow>Blogs &amp; Insights</Eyebrow>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Written by the engineers, <span className="text-[#8E9AEA]">not the marketing team.</span>
              </h2>
              <Link
                to="/insights"
                className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-white transition-colors duration-200 hover:text-[#8E9AEA]"
                data-testid="resources-all-insights-link"
              >
                All articles <ArrowRight className="h-4 w-4 text-[#8E9AEA]" />
              </Link>
            </div>
          </Reveal>
          {posts.length === 0 ? (
            <p className="mt-8 text-sm text-[#A6B0C9]" data-testid="resources-blogs-empty">
              New articles are published here regularly.
            </p>
          ) : (
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {posts.slice(0, 4).map((p, i) => (
                <PostCard key={p.slug} post={p} delay={Math.min(i, 3) * 0.06} />
              ))}
            </div>
          )}
        </div>
      </section>
      <CTABand
        title={{ pre: "Need something ", accent: "specific?" }}
        copy="Tell us what you're working on and a Lanvaus engineer will point you to the right resource, or write one."
      />
    </div>
  );
}
